import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { dealsAPI, usersAPI, organizationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/UI/UserAvatar';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  Squares2X2Icon,
  BuildingOfficeIcon,
  CalendarIcon,
  ArchiveBoxIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import Modal from '../../components/UI/Modal';

const ACTIVE_DEAL_STAGES = ['prospecting', 'qualification', 'proposal', 'negotiation'];
const ARCHIVED_DEAL_STAGES = ['closed_won', 'closed_lost'];
const ALL_DEAL_STAGES = [...ACTIVE_DEAL_STAGES, ...ARCHIVED_DEAL_STAGES];

const STAGE_COLORS = {
  prospecting: 'bg-blue-500',
  qualification: 'bg-purple-500',
  proposal: 'bg-amber-500',
  negotiation: 'bg-orange-500',
  closed_won: 'bg-emerald-500',
  closed_lost: 'bg-red-500',
};

const STAGE_LABELS = {
  prospecting: 'Prospecting',
  qualification: 'Qualification',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

const Deals = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('pipeline');
  const [activeTab, setActiveTab] = useState('active');
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    value: '',
    stage: 'prospecting',
    probability: 50,
    expectedCloseDate: '',
    assignedTo: '',
    customerOrganization: '',
    customer: {
      name: '',
      email: '',
      phone: '',
    },
  });

  const { data: deals, isLoading } = useQuery({
    queryKey: ['deals', searchTerm],
    queryFn: async () => {
      const params = searchTerm ? { search: searchTerm } : {};
      const response = await dealsAPI.getAll(params);
      return response.data.data;
    },
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersAPI.getAll();
      return response.data.data;
    },
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await organizationsAPI.getAll();
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: dealsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['deals']);
      toast.success('Deal created successfully');
      closeModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create deal');
    },
  });

  const updateStageMutation = useMutation({
    mutationFn: ({ id, stage }) => dealsAPI.update(id, { stage }),
    onError: (error) => {
      // Rollback: refetch to restore correct state
      queryClient.invalidateQueries(['deals']);
      toast.error(error.response?.data?.message || 'Failed to update deal stage');
    },
  });

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStage = destination.droppableId;

    // Optimistic update: move the card instantly in the cache
    queryClient.setQueryData(['deals', searchTerm], (old) => {
      if (!old) return old;

      // Build grouped columns from current data
      const columns = {};
      ACTIVE_DEAL_STAGES.forEach(s => { columns[s] = []; });
      old.forEach(deal => {
        if (columns[deal.stage]) columns[deal.stage].push(deal);
      });

      // Remove the dragged item from its source column
      const [movedItem] = columns[source.droppableId].splice(source.index, 1);
      if (!movedItem) return old;

      // Update the stage on the moved item
      const updatedItem = { ...movedItem, stage: newStage };

      // Insert at the destination index
      columns[destination.droppableId].splice(destination.index, 0, updatedItem);

      // Flatten back to a single array (keep archived deals too)
      const activeFlatted = ACTIVE_DEAL_STAGES.flatMap(s => columns[s]);
      const archivedDeals = old.filter(d => ARCHIVED_DEAL_STAGES.includes(d.stage));
      return [...activeFlatted, ...archivedDeals];
    });

    updateStageMutation.mutate({ id: draggableId, stage: newStage });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: '',
      description: '',
      value: '',
      stage: 'prospecting',
      probability: 50,
      expectedCloseDate: '',
      assignedTo: '',
      customerOrganization: '',
      customer: { name: '', email: '', phone: '' },
    });
  };

  const handleOrgChange = (orgId) => {
    const org = organizations?.find((o) => o._id === orgId);
    setFormData({
      ...formData,
      customerOrganization: orgId,
      customer: {
        ...formData.customer,
        name: formData.customer.name || org?.name || '',
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      value: parseFloat(formData.value),
      probability: parseInt(formData.probability),
    };
    if (!payload.assignedTo) delete payload.assignedTo;
    createMutation.mutate(payload);
  };

  const groupedDeals = ACTIVE_DEAL_STAGES.reduce((acc, stage) => {
    acc[stage] = deals?.filter((deal) => deal.stage === stage) || [];
    return acc;
  }, {});

  const archivedDeals = deals?.filter(deal => ARCHIVED_DEAL_STAGES.includes(deal.stage)) || [];

  const getTotalValue = (stage) => {
    return groupedDeals[stage]?.reduce((sum, deal) => sum + (deal.value || 0), 0) || 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Deals</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Manage your sales pipeline</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="btn btn-primary">
          <PlusIcon className="w-4 h-4 mr-1.5" />
          Add Deal
        </button>
      </div>

      {/* Search, Tab, and View Toggle */}
      <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 p-3">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search deals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-surface-700 border-0 rounded-lg focus:bg-white dark:focus:bg-surface-600 focus:ring-2 focus:ring-primary-500 transition-all placeholder-gray-400 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === 'active'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-600'
              }`}
            >
              <FunnelIcon className="w-4 h-4 mr-1" />
              Pipeline
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === 'archived'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-600'
              }`}
            >
              <ArchiveBoxIcon className="w-4 h-4 mr-1" />
              Closed
              {archivedDeals.length > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === 'archived' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}>
                  {archivedDeals.length}
                </span>
              )}
            </button>
          </div>
          {activeTab === 'active' && (
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setViewMode('pipeline')}
              className={`px-3 py-2 transition-colors ${
                viewMode === 'pipeline'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-600'
              }`}
            >
              <Squares2X2Icon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-surface-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-surface-600'
              }`}
            >
              <TableCellsIcon className="w-4 h-4" />
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Pipeline View */}
      {activeTab === 'active' && viewMode === 'pipeline' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {ACTIVE_DEAL_STAGES.map((stage) => (
              <div key={stage} className="bg-gray-50 dark:bg-surface-800 rounded-xl border border-gray-200 dark:border-surface-700">
                {/* Column Header */}
                <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${STAGE_COLORS[stage]}`} />
                      <h3 className="font-semibold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300">
                        {STAGE_LABELS[stage]}
                      </h3>
                    </div>
                    <span className="bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs font-medium">
                      {groupedDeals[stage].length}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">
                    ${getTotalValue(stage).toLocaleString()}
                  </p>
                </div>

                {/* Deal Cards */}
                <Droppable droppableId={stage}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`p-2 space-y-2 min-h-[200px] transition-colors ${
                        snapshot.isDraggingOver ? 'bg-primary-50 dark:bg-primary-900/10' : ''
                      }`}
                    >
                      {groupedDeals[stage].map((deal, index) => (
                        <Draggable key={deal._id} draggableId={deal._id} index={index}>
                          {(provided, snapshot) => {
                            const card = (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`kanban-drag-card rounded-lg transition-all duration-200 ${
                                snapshot.isDragging 
                                  ? 'is-dragging shadow-2xl rotate-2 scale-105' 
                                  : ''
                              }`}
                              style={snapshot.isDragging ? { ...provided.draggableProps.style, zIndex: 99999 } : provided.draggableProps.style}
                            >
                              <Link to={`/deals/${deal._id}`} className="block p-3">
                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1.5 line-clamp-1">
                                  {deal.name}
                                </h4>
                                <p className="text-base font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                                  ${deal.value?.toLocaleString()}
                                </p>

                                {/* Organization */}
                                {deal.customerOrganization?.name && (
                                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                                    <BuildingOfficeIcon className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                                    <span className="truncate">{deal.customerOrganization.name}</span>
                                  </div>
                                )}

                                {/* Customer name */}
                                {deal.customer?.name && !deal.customerOrganization?.name && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 truncate">
                                    {deal.customer.name}
                                  </p>
                                )}

                                {/* Meta row */}
                                <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                                  <span>{deal.probability}%</span>
                                  {deal.expectedCloseDate && (
                                    <div className="flex items-center">
                                      <CalendarIcon className="w-3 h-3 mr-1" />
                                      {new Date(deal.expectedCloseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </div>
                                  )}
                                </div>

                                {/* Assignee at bottom */}
                                {deal.assignedTo && (
                                  <div className="flex items-center mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700">
                                    <UserAvatar user={deal.assignedTo} size="xs" />
                                    <span className="ml-2 text-xs text-gray-600 dark:text-gray-400 truncate">
                                      {deal.assignedTo.firstName} {deal.assignedTo.lastName}
                                    </span>
                                  </div>
                                )}
                              </Link>
                            </div>
                            );
                            return snapshot.isDragging ? ReactDOM.createPortal(card, document.body) : card;
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Table View (active deals only) */}
      {activeTab === 'active' && viewMode === 'table' && (
        <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Deal</th>
                  <th>Organization</th>
                  <th>Value</th>
                  <th>Stage</th>
                  <th>Probability</th>
                  <th>Close Date</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const activeDeals = deals?.filter(d => ACTIVE_DEAL_STAGES.includes(d.stage)) || [];
                  return activeDeals.length > 0 ? (
                  activeDeals.map((deal) => (
                    <tr key={deal._id}>
                      <td>
                        <Link to={`/deals/${deal._id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
                          {deal.name}
                        </Link>
                      </td>
                      <td>
                        {deal.customerOrganization ? (
                          <div className="flex items-center">
                            <BuildingOfficeIcon className="w-4 h-4 mr-1.5 text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{deal.customerOrganization.name}</span>
                          </div>
                        ) : deal.customer?.name ? (
                          <span className="text-sm text-gray-500">{deal.customer.name}</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-sm font-semibold text-gray-900 dark:text-white">
                        ${deal.value?.toLocaleString()}
                      </td>
                      <td>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                          {STAGE_LABELS[deal.stage]}
                        </span>
                      </td>
                      <td className="text-sm text-gray-600 dark:text-gray-400">{deal.probability}%</td>
                      <td className="text-sm text-gray-600 dark:text-gray-400">
                        {deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        {deal.assignedTo ? (
                          <div className="flex items-center">
                            <UserAvatar user={deal.assignedTo} size="xs" className="mr-2" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {deal.assignedTo.firstName} {deal.assignedTo.lastName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">No active deals found</h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new deal.</p>
                    </td>
                  </tr>
                );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Closed/Archived Deals Table */}
      {activeTab === 'archived' && (
        <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 overflow-hidden">
          {archivedDeals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr>
                    <th>Deal</th>
                    <th>Organization</th>
                    <th>Value</th>
                    <th>Outcome</th>
                    <th>Probability</th>
                    <th>Close Date</th>
                    <th>Owner</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedDeals.map((deal) => (
                    <tr key={deal._id} className="hover:bg-gray-50 dark:hover:bg-surface-700 transition-colors">
                      <td>
                        <Link to={`/deals/${deal._id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
                          {deal.name}
                        </Link>
                      </td>
                      <td>
                        {deal.customerOrganization ? (
                          <div className="flex items-center">
                            <BuildingOfficeIcon className="w-4 h-4 mr-1.5 text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{deal.customerOrganization.name}</span>
                          </div>
                        ) : deal.customer?.name ? (
                          <span className="text-sm text-gray-500">{deal.customer.name}</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-sm font-semibold text-gray-900 dark:text-white">
                        ${deal.value?.toLocaleString()}
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          deal.stage === 'closed_won'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {STAGE_LABELS[deal.stage]}
                        </span>
                      </td>
                      <td className="text-sm text-gray-600 dark:text-gray-400">{deal.probability}%</td>
                      <td className="text-sm text-gray-600 dark:text-gray-400">
                        {deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        {deal.assignedTo ? (
                          <div className="flex items-center">
                            <UserAvatar user={deal.assignedTo} size="xs" className="mr-2" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {deal.assignedTo.firstName} {deal.assignedTo.lastName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-sm text-gray-500 dark:text-gray-400">
                        {deal.updatedAt ? new Date(deal.updatedAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <ArchiveBoxIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">No closed deals</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Won and lost deals will appear here.</p>
            </div>
          )}
        </div>
      )}

      {/* Create Deal Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title="Create New Deal" size="lg">
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="label">Deal Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="glass-input"
              placeholder="Enter deal name"
            />
          </div>

          {/* Organization */}
          <div>
            <label className="label">Organization *</label>
            <select
              required
              value={formData.customerOrganization}
              onChange={(e) => handleOrgChange(e.target.value)}
              className="glass-input"
            >
              <option value="">Select organization</option>
              {organizations?.map((org) => (
                <option key={org._id} value={org._id}>
                  {org.name} {org.industry ? `(${org.industry})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Customer Contact */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Contact Person *</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.customer.name}
                  onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, name: e.target.value } })}
                  className="glass-input"
                  placeholder="Contact name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.customer.email}
                  onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, email: e.target.value } })}
                  className="glass-input"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Phone</label>
                <input
                  type="tel"
                  value={formData.customer.phone}
                  onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, phone: e.target.value } })}
                  className="glass-input"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Value ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="glass-input"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="label">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                className="glass-input"
              >
                {ALL_DEAL_STAGES.map((stage) => (
                  <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Probability (%) *</label>
              <input
                type="number"
                min="0"
                max="100"
                required
                value={formData.probability}
                onChange={(e) => setFormData({ ...formData, probability: e.target.value })}
                className="glass-input"
              />
            </div>
            <div>
              <label className="label">Expected Close Date *</label>
              <input
                type="date"
                required
                value={formData.expectedCloseDate}
                onChange={(e) => setFormData({ ...formData, expectedCloseDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="glass-input"
              />
            </div>
            <div>
              <label className="label">Assigned To</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                className="glass-input"
              >
                <option value="">Unassigned</option>
                {users?.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="glass-input"
              placeholder="Enter deal description"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button type="button" onClick={closeModal} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create Deal'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Deals;






